/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */
/**
 * SlideoutOriginContext — tells a component WHICH slideout-stack entry it is
 * rendered inside, or `null` when it renders on an app surface (a page, a
 * list, the chat, a settings section).
 *
 * This exists for one product rule (see `useOpenSlideout`): a click FROM THE
 * APP replaces the open slideouts; only a click from INSIDE a rendered
 * slideout builds the stack. Shared components (entity badges, markdown
 * entity links, timeline events, record sections) render in both worlds, so
 * they cannot know statically — the host declares it.
 *
 * Providers are mounted ONLY by components that render a stack entry's
 * content:
 *  - `CrmObjectSlideout` (entity overlay slideout)
 *  - `inline-crm-object-panel` (entity panel inline-hosted by a page)
 *  - `EmailThreadPanel` (email-thread slideout)
 *  - `ProposalDetailPanel` (proposal — in every variant)
 *
 * Do NOT mount one from a page or an app pane — absence of a provider is
 * what marks a click as app-level.
 */

import { createContext, type ReactNode, useContext } from 'react';

// Type-only import — no runtime cycle with the stack context.
import type { SlideoutEntry } from './SlideoutStackContext';

const SlideoutOriginContext = createContext<SlideoutEntry | null>(null);

/**
 * The stack entry this component is rendered inside, or `null` on an app
 * surface.
 */
export function useSlideoutOrigin(): SlideoutEntry | null {
  return useContext(SlideoutOriginContext);
}

/**
 * Declares that everything beneath renders inside the given stack entry.
 * Mounted only by stack-entry renderers — see the module doc.
 */
export function SlideoutOriginProvider({
  entry,
  children,
}: {
  entry: SlideoutEntry;
  children: ReactNode;
}) {
  return (
    <SlideoutOriginContext value={entry}>{children}</SlideoutOriginContext>
  );
}
